import { ComponentPropsWithoutRef, forwardRef, useState } from 'react';
import { TextInput } from 'melp-design/components';

// dot or comma is used as a separator only once in the float number
const FLOAT_REGEX = /^([+-]?\d+([.,]\d*)?|-)$/;
// match strings that consist entirely of one or more digits
const INTEGER_REGEX = /^[0-9]+$/;

type ValueType = 'float' | 'integer';

interface NumberInputProps {
  key: number;
  value: number;
  onChange: (value: number) => void;
  onInputChange?: (value: string) => void;
  type?: ValueType;
}

const REGEX_BY_VALUE_TYPE: Record<ValueType, RegExp> = {
  float: FLOAT_REGEX,
  integer: INTEGER_REGEX,
};

type Props = Omit<
  ComponentPropsWithoutRef<typeof TextInput>,
  'multiline' | 'secure' | 'emoji' | keyof NumberInputProps
> &
  NumberInputProps;

/** Provide stable "key" prop to have value in sync */
export const NumberInput = forwardRef<HTMLInputElement, Props>(
  (
    { value, onChange, onInputChange, type = 'float', ...textInputProps },
    forwardedRed,
  ) => {
    const [displayedValue, setDisplayedValue] = useState(
      Number.isNaN(value) ? '' : String(value),
    );

    const updateDisplayedValue = (next: string) => {
      setDisplayedValue(next);
      onInputChange?.(next);
    };

    return (
      <TextInput
        value={displayedValue}
        onChange={(value) => {
          if (!value || REGEX_BY_VALUE_TYPE[type].test(value)) {
            updateDisplayedValue(value);
          }
        }}
        onBlur={({ target }) => {
          const parsed = Number(target.value.replace(',', '.'));
          onChange(parsed);
          updateDisplayedValue(String(parsed));
        }}
        {...textInputProps}
        ref={forwardedRed}
      />
    );
  },
);

NumberInput.displayName = 'NumberInput';
